import { type UIMessage, type UseChatHelpers, useChat } from "@ai-sdk/react";
import { getAuthRequestHeaders } from "@qw/auth";
import type { DefaultPromptInput } from "@qw/graphql";
import type { ElicitResponse, MCPPlatform } from "@qw/qw-common";
import { DefaultChatTransport } from "ai";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ExtendedUIMessage } from "../chat.types";
import { createChatUserMessageId } from "../chat-message-id";
import { throwIfSendWasRefused } from "../chat-send-refusal";
import { selectMessagesAfterResolvingElicit } from "../ephemeral-slot";
import type { ToolFilter } from "../providers/chat-provider";
import { getToolFilter } from "../utils";
import { ElicitationSubmitPhase } from "./elicitation-submit-phase";
import { useChatRenderList } from "./use-chat-render-list";
import { useEvictSettledTurns } from "./use-evict-settled-turns";
import { useHeldResolvedTurns } from "./use-held-resolved-turns";
import { STREAM_THROTTLE_MS } from "./use-smooth-streamed-text";
import { useStreamAttach } from "./use-stream-attach";

/**
 * Grace period (in milliseconds) to wait after signaling abort before stopping the stream listener.
 * This allows the frontend to receive:
 * - Any final data chunks already generated but still in flight
 * - The system-generated abort message from the backend
 *
 * Trade-off: Slightly delays the perceived responsiveness of the abort button (250ms is barely perceptible)
 * but ensures better UX by showing the abort message and capturing paid-for data.
 */
const ABORT_GRACE_PERIOD_MS = 250;

export interface UseMcpChatProps {
  apiUrl?: string;
  isReadOnly?: boolean;
  sessionId: string;
  systemPrompt: string;
  onError?: (error: Error) => void;
  onResponse?: (message: UIMessage) => void;
  onFinish?: (message: UIMessage) => void;
  /** Async work started when a real SDK stream settles (the provider's whole-tree history refetch). */
  onStreamSettled?: () => Promise<void>;
  platform?: MCPPlatform;
  promptInput?: DefaultPromptInput;
  toolFilter?: ToolFilter | null;
  toolFilterStorageKey?: string;
  /**
   * The server-authoritative persisted history tree (brief 011 PR 5) — the render base. Empty until
   * the whole-tree query resolves; the provider surfaces the loading/error states separately via
   * `historyStatus`. Server-authoritative render is the only path (brief 015), so this is used
   * directly as the base — a mid-stream update is reconciled by `composeSlotRenderMessages`.
   */
  serverHistory: ExtendedUIMessage[];
  /** Whether this browser should attach to and publish the live token stream into React. */
  liveStreamEnabled?: boolean;
}

export interface UseMcpChatReturn
  extends Pick<UseChatHelpers<ExtendedUIMessage>, "stop" | "regenerate" | "status" | "error" | "sendMessage"> {
  // The render list is the server-authoritative composed output (`renderMessages`). `useChat` is now
  // instantiated as `useChat<ExtendedUIMessage>`, so the buffer, `sendMessage`, etc. are all typed to
  // `ExtendedUIMessage` end-to-end (renderKey + typed metadata) — no `UIMessage → ExtendedUIMessage`
  // casts at the render/hold seams.
  messages: ExtendedUIMessage[];
  // Convenience properties
  isReady: boolean;
  isLoading: boolean;
  isError: boolean;

  // Structured elicitation
  isElicitationSubmitting: boolean;
  elicitationSubmitPhase: ElicitationSubmitPhase;
  submitElicitationResponse: (toolCallId: string, response: ElicitResponse) => Promise<void>;
}

export const useMcpChat = ({
  apiUrl = location.origin,
  isReadOnly = false,
  sessionId,
  systemPrompt,
  onError = (error) => {
    // biome-ignore lint/suspicious/noConsole: default fallback logging keeps existing hook behavior.
    console.error("Chat error:", error); // eslint-disable-line no-console
  },
  onResponse = (_) => {},
  onFinish = (message) => {
    // biome-ignore lint/suspicious/noConsole: default fallback logging keeps existing hook behavior.
    console.log("Message finished:", message); // eslint-disable-line no-console
  },
  onStreamSettled,
  platform,
  promptInput,
  toolFilter,
  toolFilterStorageKey,
  serverHistory,
  liveStreamEnabled = true,
}: UseMcpChatProps): UseMcpChatReturn & { ensureAttached: () => void } => {
  // Server-authoritative render is the only path (brief 015): `serverHistory` (the persisted
  // whole-tree base) is passed in and used directly — no applied-mode latch or adoption gating,
  // since there is no legacy↔server switch to re-key `useChat` around. A mid-stream base update is
  // reconciled by `composeSlotRenderMessages`.

  // Use ref to store current platform to avoid stale closures in customTransport
  const platformRef = useRef<MCPPlatform | undefined>(platform);
  const promptInputRef = useRef<DefaultPromptInput | undefined>(promptInput);
  promptInputRef.current = promptInput;

  // Keep refs in sync with state/props
  useEffect(() => {
    platformRef.current = platform;
  }, [platform]);

  // Server-authoritative render: seed `useChat` with the system message only. History lives in
  // `serverHistory` (rendered outside the SDK); `useChat` holds only the live in-flight turns.
  const initialMessagesWithSystemPrompt = useMemo<ExtendedUIMessage[]>(
    () => [{ id: "1", role: "system" as const, parts: [{ type: "text", text: systemPrompt }] }],
    [systemPrompt]
  );

  // `useChat` re-initializes only when its `id` changes. Because the seed is system-only and history
  // comes from `serverHistory` (not a `useChat` re-seed), the key only needs to change on a session
  // switch — there is no content-driven re-init. The `-sh` suffix is retained so keys stay distinct
  // from any legacy-era cached key.
  const chatKey = useMemo(() => `${sessionId}-sh`, [sessionId]);

  // Custom transport that injects timezone, platform, promptInput, and toolFilter
  // into outbound POST bodies. The resume GET path (prepareReconnectToStreamRequest)
  // passes through unmodified — the server looks up the active stream purely by
  // sessionId in the URL.
  const customTransport = useMemo(
    () =>
      new DefaultChatTransport({
        api: `${apiUrl}/api/v2/chat/sessions/${sessionId}/messages`,
        headers: getAuthRequestHeaders(),
        prepareReconnectToStreamRequest: () => ({
          api: `${apiUrl}/api/v2/chat/sessions/${sessionId}/messages`,
          method: "GET",
          headers: getAuthRequestHeaders(),
        }),
        // Intercept POSTs to add timezone/platform/promptInput/toolFilter to the body.
        fetch: async (url, options) => {
          if (options?.method === "POST" && options?.body) {
            const body = JSON.parse(options.body as string) as Record<string, unknown>;

            // Automatically detect and include the user's current timezone
            try {
              body.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
            } catch {
              // Fallback to UTC if timezone detection fails
              body.timezone = "UTC";
            }

            body.platform = platformRef.current;
            body.promptInput = promptInputRef.current;

            // Fixed tool filters take precedence over user-selected filters stored for this chat.
            const storageKey = toolFilterStorageKey || sessionId;
            const effectiveToolFilter = toolFilter === undefined ? getToolFilter(storageKey) : toolFilter;
            if (effectiveToolFilter) {
              body.toolFilter = effectiveToolFilter;
            }

            options.body = JSON.stringify(body);
          }

          const response = await fetch(url, options);

          // Last point where the status is still readable — the SDK discards it. See
          // `throwIfSendWasRefused`; every other status falls through unchanged.
          throwIfSendWasRefused(options, response);

          return response;
        },
      }),
    [apiUrl, sessionId, toolFilter, toolFilterStorageKey]
  );

  const {
    messages,
    setMessages,
    sendMessage: originalSendMessage,
    stop: originalStop,
    resumeStream,
    regenerate: originalRegenerate,
    status,
    error,
  } = useChat<ExtendedUIMessage>({
    id: chatKey,
    // Mint new user-message ids in the `uimsg_` namespace (brief 011 Piece 1). The
    // server persists the client id verbatim as `externalMessageId`, so a turn's
    // streamed and persisted copies share an id. Assistant-turn ids still come from
    // the server stream, not this generator. Every `ChatProvider` consumer inherits
    // it, so there is no per-surface send-path wiring to do.
    generateId: createChatUserMessageId,
    // The smooth reveal (useSmoothStreamedText) handles per-character pacing;
    // this just keeps the reveal's target buffer fresh. See STREAM_THROTTLE_MS.
    experimental_throttle: STREAM_THROTTLE_MS,
    // We drive stream attach imperatively via the mount probe + subscription
    // `onData` handler below. Leaving the AI SDK's declarative `resume:` prop
    // off avoids its built-in `useEffect(() => { if (resume) resumeStream(); }, [resume])`
    // firing reactively whenever any input changes.
    resume: false,
    // Backend handles all tool execution autonomously
    transport: customTransport,
    messages: initialMessagesWithSystemPrompt,
    onError,
    onFinish({ message }) {
      // Reconcile persisted history after the stream settles (the provider's whole-tree refetch).
      // Fire-and-forget — swallow rejection so a failed refetch can't surface as an unhandled
      // rejection (the failure is reflected in the next `serverHistory`/`historyStatus`).
      try {
        void (onStreamSettled?.() ?? Promise.resolve()).catch(() => undefined);
      } catch {
        // onStreamSettled threw synchronously — ignore; history stays at its last-good value.
      }
      onFinish(message);
      onResponse(message);
    },
  });

  // Latest `useChat.messages`, read (not subscribed) inside the imperative elicit-submit callback so
  // it can capture the resolved turn without adding `messages` to the callback's deps.
  const messagesRef = useRef(messages);
  messagesRef.current = messages;

  // B-1445 held-turns: the render-only holding area, owned end-to-end by `useHeldResolvedTurns`
  // (insertion, prune convergence, and cross-chat clear all live in that one hook). When an elicit
  // resolve drops the settled turn from the `useChat` buffer for a clean resume seed, the turn is
  // parked there (keyed by `renderKey`) so the render keeps showing it through the
  // settle→history-refetch window — then released the moment its authoritative `serverHistory` row
  // lands (gap-free by `renderKey`). Parking happens via `holdTurnsResolvingElicit` in
  // `submitElicitationResponse`; the render base injects `heldResolvedTurns` below.
  const { heldResolvedTurns, holdTurnsResolvingElicit } = useHeldResolvedTurns({ serverHistory, chatKey });

  // Structured elicitation submission state — lifted above the subscription
  // so its `onData` handler can suppress concurrent resumes while the
  // imperative `submitElicitationResponse` flow is mid-flight (see
  // `shouldFireOnStreamStateEvent` for the race rationale).
  //
  // The ref is the authoritative gate signal for the subscription. It is
  // mutated SYNCHRONOUSLY at the top of `submitElicitationResponse` (before
  // any await) and cleared in the `finally`, so the gate flips to `true` in
  // the same tick as the POST starts — there's no render-commit gap during
  // which an incoming stream-state event could observe a stale `false` and
  // race a second `resumeStream()` against the imperative one.
  //
  // `isElicitationSubmitting` is the boolean public-API surface (used by
  // the chat form for disabled-button UX and by callers that just want
  // "is a submit happening at all"). Derive it from the finer-grained
  // phase used by the indicator components to switch copy from
  // "Submitting response..." (during the POST) to "Alby is thinking..."
  // (POST resolved, awaiting the resumed stream's first chunks). The
  // subscription closure reads neither value; it uses the synchronous ref.
  const [elicitationSubmitPhase, setElicitationSubmitPhase] = useState<ElicitationSubmitPhase>(
    ElicitationSubmitPhase.Idle
  );
  const isElicitationSubmitting = elicitationSubmitPhase !== ElicitationSubmitPhase.Idle;
  const isElicitationSubmittingRef = useRef(false);

  // (The interrupted-notice delivery bridge was removed with the render-mode collapse — brief 015.
  // Server-authoritative history carries interrupted-run notices in the `conversationMessages` tree,
  // so there is no out-of-band notice to reconcile into `useChat` anymore.)

  // B-1445 (S3): evict turns from the `useChat` buffer once they are durably in `serverHistory`.
  // Keeps the buffer minimal and — critically — leaves a non-assistant tail before an elicit-resume
  // so `ai@6` seeds the continuation cleanly (no cloned `tool-elicit` part). `renderKey` identity
  // (S2) makes the eviction gap-free.
  useEvictSettledTurns({
    status,
    messages,
    serverHistory,
    setMessages,
    isElicitationSubmittingRef,
  });

  // SSE stream-attach lifecycle (mount probe + server-push subscription + tab-return/WS-reconnect
  // re-attach) and the synthetic `streamFailureError`, owned by `useStreamAttach`.
  //
  // Effect-order note (B-1458): extracting the mount probe into this hook moved it AFTER
  // `useEvictSettledTurns` in declaration order (React runs passive effects in that order), whereas
  // it was registered before eviction inline. This is safe: on the only commits where both effects
  // fire — first mount and a `chatKey` (session) switch — the `useChat` buffer holds just the injected
  // system message (`messages[0]`, always retained by `selectMessagesAfterEviction`), so eviction is a
  // no-op with nothing for the probe to race. The two never contend over a real turn, so their
  // relative order does not affect behavior.
  const { ensureAttached, streamFailureError } = useStreamAttach({
    isReadOnly,
    sessionId,
    chatKey,
    status,
    resumeStream,
    originalStop,
    isElicitationSubmitting,
    isElicitationSubmittingRef,
    liveStreamEnabled,
  });

  const sendMessage: typeof originalSendMessage = useCallback(
    async (...args) => {
      if (isReadOnly) {
        throw new Error("This chat is read-only.");
      }

      return originalSendMessage(...args);
    },
    [isReadOnly, originalSendMessage]
  );

  const regenerate: typeof originalRegenerate = useCallback(
    async (...args) => {
      if (isReadOnly) {
        throw new Error("This chat is read-only.");
      }

      return originalRegenerate(...args);
    },
    [isReadOnly, originalRegenerate]
  );

  // Custom stop function that calls the abort endpoint. Server keys the abort
  // flag on chatSessionId (see chat.controller.ts `abortStream`), so no
  // client-side requestId is needed.
  const stop = useCallback(async () => {
    if (isReadOnly) {
      return;
    }

    try {
      await fetch(`${apiUrl}/api/v2/chat/sessions/${sessionId}/abort`, {
        method: "DELETE",
        headers: getAuthRequestHeaders(),
      });

      // Wait for grace period to receive any final chunks (including the abort message).
      // This allows us to capture data already paid for and the system-generated abort message.
      await new Promise((resolve) => setTimeout(resolve, ABORT_GRACE_PERIOD_MS));
    } catch (error) {
      // Network errors during abort are acceptable (stream might already be done)
      // Only log for debugging purposes
      // biome-ignore lint/suspicious/noConsole: abort failures are intentionally debug-only.
      console.debug("Abort request failed:", error); // eslint-disable-line no-console
    }
    // Now stop listening to the stream
    void originalStop();
  }, [isReadOnly, apiUrl, sessionId, originalStop]);

  const submitElicitationResponse = useCallback(
    async (toolCallId: string, elicitResponse: ElicitResponse) => {
      if (isReadOnly) {
        throw new Error("This chat is read-only.");
      }

      // Flip the gate ref BEFORE any await. The subscription's `onData`
      // reads this ref directly (not the React-rendered state), so the
      // suppression is in effect by the time the POST starts. Without
      // this — i.e., relying on a render-time `ref.current = state`
      // assignment — the gate would still be `false` in the
      // microtask-flush window between `setElicitationSubmitPhase(Posting)`
      // and React's next commit, and a fast server publishing the
      // live-stream event in that window would slip through.
      isElicitationSubmittingRef.current = true;
      // Phase 1: the POST to `/elicitations/.../resolve` is in flight.
      // Indicator copy: "Submitting response..." — frames the user as
      // the actor, accurate while the network round trip is in flight.
      setElicitationSubmitPhase(ElicitationSubmitPhase.Posting);
      try {
        // Include the active tool filter so elicitation resumes with the same tool restrictions.
        const storageKey = toolFilterStorageKey || sessionId;
        const effectiveToolFilter = toolFilter === undefined ? getToolFilter(storageKey) : toolFilter;
        const res = await fetch(`${apiUrl}/api/v2/chat/sessions/${sessionId}/elicitations/${toolCallId}/resolve`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...getAuthRequestHeaders(),
          },
          body: JSON.stringify({
            ...elicitResponse,
            platform: platformRef.current,
            ...(effectiveToolFilter && { toolFilter: effectiveToolFilter }),
          }),
        });

        if (!res.ok) {
          const contentType = res.headers.get("content-type") || "";
          if (contentType.includes("application/json")) {
            const body = (await res.json().catch(() => ({}))) as { message?: string };
            throw new Error(body.message || `Failed to submit elicitation (${res.status})`);
          }
          throw new Error(`Failed to submit elicitation (${res.status})`);
        }

        // Phase 2: server got the response and has kicked off the resume
        // orchestrate. We're now waiting for the resumed stream's first
        // chunk. Indicator copy: "Alby is thinking..." — frames Alby
        // as the actor, accurate while the LLM is preparing its reply.
        setElicitationSubmitPhase(ElicitationSubmitPhase.WaitingForStream);

        // B-1445 (S3): synchronous clean-seed, without blanking. The effect-based eviction can only
        // drop the settled elicit turn once it is in `serverHistory`, but the SDK goes idle before the
        // post-settlement refetch lands — so a fast submit can leave the `tool-elicit` turn at the
        // `useChat` tail and `ai@6` would seed the resume by cloning it. We must remove it from the
        // buffer for a clean seed, but it may be the ONLY rendered copy (its `serverHistory` row hasn't
        // landed), so we first park it in the render-only held area, then drop it from the buffer. The
        // held copy keeps the bubble rendered until its authoritative row lands (gap-free by
        // `renderKey`), so the seed is clean AND nothing blanks — by construction, not by timing.
        // `useHeldResolvedTurns` owns the parking (and its release); drop the parked turn(s) from the
        // `useChat` buffer only when something was actually parked.
        const parked = holdTurnsResolvingElicit(messagesRef.current, toolCallId);
        if (parked.length > 0) {
          setMessages((current) => selectMessagesAfterResolvingElicit(current, toolCallId));
        }

        // Imperatively trigger stream reconnection and wait for it to complete.
        // resumeStream() hits the GET endpoint which looks up the new active
        // stream by sessionId. Once chunks start arriving, the AI SDK status
        // flips to `streaming` and the indicator switches to the existing
        // "Alby is responding..." copy.
        if (liveStreamEnabled) {
          await resumeStream();
        }
      } finally {
        // Clear the gate ref synchronously with the state update so a
        // late `chatSessionLiveStreamStarted` event (e.g., from a
        // follow-up elicitation pause inside the same orchestrate run)
        // can re-fire the subscription handler.
        isElicitationSubmittingRef.current = false;
        setElicitationSubmitPhase(ElicitationSubmitPhase.Idle);
      }
    },
    [
      apiUrl,
      isReadOnly,
      sessionId,
      toolFilter,
      toolFilterStorageKey,
      resumeStream,
      setMessages,
      holdTurnsResolvingElicit,
      liveStreamEnabled,
    ]
  );

  // Prefer the synthetic stream-failure error over the AI SDK's own
  // `error`. The two are independent sources: AI SDK populates its
  // `error` when the SSE stream itself errors (stream-side onError
  // callback); the synthetic value is populated from server-published
  // FAILED stream-state events (brief 004 PR2) — covering pre-admit
  // failures where no SSE stream ever connected. When both fire (e.g.
  // a post-mint failure where the stream erred AND the server
  // published FAILED), the synthetic value wins because its message
  // came from the canonical server-side error payload while the
  // SSE-side error is generic. `isError` widens to include both.
  const combinedError = streamFailureError ?? error;

  // Server-authoritative render list — the `serverHistory` (persisted base) + held-turns + ephemeral
  // slot composition. Owned by `useChatRenderList`; see it for the base/slot/streaming-turn model.
  const renderMessages = useChatRenderList({ serverHistory, heldResolvedTurns, messages, status });

  return {
    messages: renderMessages,
    stop,
    sendMessage,
    regenerate,
    status,
    error: combinedError,

    isReady: status === "ready",
    isLoading: status === "streaming" || status === "submitted",
    isError: status === "error" || streamFailureError !== null,
    // Structured elicitation
    isElicitationSubmitting,
    elicitationSubmitPhase,
    submitElicitationResponse,
    ensureAttached,
  };
};

export { type AiSdkChatStatus, actionForStreamStateEvent } from "./action-for-stream-state-event";
export { ElicitationSubmitPhase } from "./elicitation-submit-phase";
